import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import TransactionDetailModal from './TransactionDetailModal';
import './Transactions.css';

function Transactions() {
  const { wallets, selectedWallets, getSelectedWalletsDisplay, getSelectedCurrency, getCurrencySymbol } = useWallets();
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [householdExpenses, setHouseholdExpenses] = useState([]);
  const [pendingIncome, setPendingIncome] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [upcomingRecurring, setUpcomingRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [currentUserId, setCurrentUserId] = useState(null);

  const selectedCurrency = getSelectedCurrency();
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      fetchTransactions();
    }
  }, [currentUserId]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/profile');
      setCurrentUserId(response.data.id);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  // Get user's split amount from transaction (for household expenses)
  const getUserSplitAmount = (transaction) => {
    if (!transaction.split_config || !currentUserId) {
      return transaction.amount;
    }

    try {
      const splitConfig = typeof transaction.split_config === 'string' 
        ? JSON.parse(transaction.split_config) 
        : transaction.split_config;
      
      const userSplit = splitConfig.find(split => split.user_id === currentUserId);
      return userSplit ? userSplit.split_amount : transaction.amount;
    } catch (error) {
      console.error('Error parsing split config:', error);
      return transaction.amount;
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Get user's households first
      const householdsRes = await api.get('/household');
      const userHouseholds = householdsRes.data;
      console.log('[Transactions] User households:', userHouseholds);
      
      // Fetch personal transactions
      const [incomeRes, expensesRes] = await Promise.all([
        api.get('/personal/income?limit=1000'),
        api.get('/personal/expenses?limit=1000')
      ]);
      
      // Fetch household expenses for each household the user belongs to
      let allHouseholdExpenses = [];
      for (const household of userHouseholds) {
        try {
          // Use expenses-with-splits endpoint to get expenses with their split info
          const householdExpRes = await api.get(`/household/${household.id}/expenses-with-splits?limit=1000`);
          console.log(`[Transactions] Household ${household.id} expenses with splits:`, householdExpRes.data);
          
          // Process each expense to add user's split info
          const expensesWithUserSplit = householdExpRes.data.map(exp => {
            // Find current user's split
            let userSplitAmount = exp.amount;
            let splitPercentage = 100;
            
            if (exp.splits && exp.splits.length > 0) {
              const userSplit = exp.splits.find(s => s.user_id === currentUserId);
              if (userSplit) {
                userSplitAmount = userSplit.split_amount;
                splitPercentage = Math.round((userSplit.split_amount / exp.amount) * 100);
              }
            }
            
            return {
              ...exp,
              household_name: household.name,
              household_id: household.id,
              is_household_expense: true,
              user_split_amount: userSplitAmount,
              split_percentage: splitPercentage
            };
          });
          
          allHouseholdExpenses = [...allHouseholdExpenses, ...expensesWithUserSplit];
        } catch (err) {
          console.error(`Error fetching expenses for household ${household.id}:`, err);
          // Fallback: try the regular endpoint without splits
          try {
            const fallbackRes = await api.get(`/household/${household.id}/expenses?limit=1000`);
            const expensesWithHousehold = fallbackRes.data.map(exp => ({
              ...exp,
              household_name: household.name,
              household_id: household.id,
              is_household_expense: true,
              user_split_amount: exp.amount,
              split_percentage: 100
            }));
            allHouseholdExpenses = [...allHouseholdExpenses, ...expensesWithHousehold];
          } catch (fallbackErr) {
            console.error(`Fallback also failed for household ${household.id}:`, fallbackErr);
          }
        }
      }
      
      console.log('[Transactions] All household expenses loaded:', allHouseholdExpenses);
      
      // Separate pending and confirmed transactions
      const allIncome = incomeRes.data;
      const allExpenses = expensesRes.data;
      
      setIncome(allIncome.filter(t => !t.is_pending));
      setExpenses(allExpenses.filter(t => !t.is_pending));
      setHouseholdExpenses(allHouseholdExpenses);
      setPendingIncome(allIncome.filter(t => t.is_pending));
      setPendingExpenses(allExpenses.filter(t => t.is_pending));
      
      // Fetch upcoming recurring transactions (next 7 days)
      try {
        const upcomingRes = await api.get('/recurring/upcoming?days=7');
        console.log('[Transactions] Upcoming recurring:', upcomingRes.data);
        // Filter out any that already have pending transactions generated
        const pendingIds = [...allIncome, ...allExpenses]
          .filter(t => t.is_pending && t.recurring_id)
          .map(t => t.recurring_id);
        const filteredUpcoming = upcomingRes.data.filter(r => !pendingIds.includes(r.recurring_id));
        setUpcomingRecurring(filteredUpcoming);
      } catch (err) {
        console.error('Error fetching upcoming recurring:', err);
        setUpcomingRecurring([]);
      }
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (transaction) => {
    const type = transaction.type;
    const id = transaction.id;
    
    if (!window.confirm(`Are you sure you want to delete this ${type === 'household_expense' ? 'household expense' : type}?`)) {
      return;
    }

    try {
      if (type === 'income') {
        await api.delete(`/personal/income/${id}`);
      } else if (type === 'household_expense') {
        await api.delete(`/household/${transaction.household_id}/expenses/${id}`);
      } else {
        await api.delete(`/personal/expenses/${id}`);
      }
      fetchTransactions();
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Failed to delete ${type}`);
    }
  };

  const handleConfirmPending = async (id, type) => {
    try {
      await api.post(`/recurring/confirm/${id}?transaction_type=${type}`);
      alert('Transaction confirmed!');
      fetchTransactions();
    } catch (error) {
      console.error('Error confirming transaction:', error);
      alert('Failed to confirm transaction');
    }
  };

  const getFilteredTransactions = () => {
    let filteredIncome = income;
    let filteredExpenses = expenses;
    let filteredHouseholdExpenses = householdExpenses;

    // Wallet filter - household expenses don't have wallet_id, show them when household wallet is selected
    if (!selectedWallets.includes('all')) {
      filteredIncome = filteredIncome.filter(t => selectedWallets.includes(t.wallet_id));
      filteredExpenses = filteredExpenses.filter(t => selectedWallets.includes(t.wallet_id));
      // For household expenses, check if any selected wallet belongs to the household
      const householdWalletIds = wallets
        .filter(w => w.wallet_type === 'household' && selectedWallets.includes(w.id))
        .map(w => w.household_id);
      filteredHouseholdExpenses = filteredHouseholdExpenses.filter(t => 
        householdWalletIds.includes(t.household_id)
      );
    }

    // Month/Year filter
    if (monthFilter !== 'all' && yearFilter !== 'all') {
      // Specific month and year
      filteredIncome = filteredIncome.filter(t => {
        const date = new Date(t.date);
        return date.getMonth() === parseInt(monthFilter) && date.getFullYear() === parseInt(yearFilter);
      });
      filteredExpenses = filteredExpenses.filter(t => {
        const date = new Date(t.date);
        return date.getMonth() === parseInt(monthFilter) && date.getFullYear() === parseInt(yearFilter);
      });
      filteredHouseholdExpenses = filteredHouseholdExpenses.filter(t => {
        const date = new Date(t.date);
        return date.getMonth() === parseInt(monthFilter) && date.getFullYear() === parseInt(yearFilter);
      });
    } else if (yearFilter !== 'all') {
      // Specific year, all months
      filteredIncome = filteredIncome.filter(t => new Date(t.date).getFullYear() === parseInt(yearFilter));
      filteredExpenses = filteredExpenses.filter(t => new Date(t.date).getFullYear() === parseInt(yearFilter));
      filteredHouseholdExpenses = filteredHouseholdExpenses.filter(t => new Date(t.date).getFullYear() === parseInt(yearFilter));
    }
    // If both are 'all', show all transactions (no date filter)

    // Type filter
    if (filter === 'income') {
      return [...filteredIncome.map(t => ({ ...t, type: 'income' }))];
    } else if (filter === 'expenses') {
      return [
        ...filteredExpenses.map(t => ({ ...t, type: 'expense' })),
        ...filteredHouseholdExpenses.map(t => ({ ...t, type: 'household_expense' }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (filter === 'household') {
      return [...filteredHouseholdExpenses.map(t => ({ ...t, type: 'household_expense' }))]
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    } else {
      return [
        ...filteredIncome.map(t => ({ ...t, type: 'income' })),
        ...filteredExpenses.map(t => ({ ...t, type: 'expense' })),
        ...filteredHouseholdExpenses.map(t => ({ ...t, type: 'household_expense' }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  };

  const getPendingTransactions = () => {
    let pending = [
      ...pendingIncome.map(t => ({ ...t, type: 'income', is_pending: true })),
      ...pendingExpenses.map(t => ({ ...t, type: 'expense', is_pending: true }))
    ];
    
    // Add upcoming recurring transactions (not yet generated as pending)
    const upcoming = upcomingRecurring.map(r => ({
      id: `recurring-${r.recurring_id}`,
      recurring_id: r.recurring_id,
      wallet_id: r.wallet_id,
      type: r.type || 'expense',
      category: r.category,
      subcategory: r.subcategory,
      description: r.description,
      amount: r.amount,
      net_amount: r.amount, // for income display
      date: r.date, // The endpoint returns 'date' not 'next_date'
      is_pending: false,
      is_upcoming: true
    }));
    
    pending = [...pending, ...upcoming];

    // Wallet filter
    if (!selectedWallets.includes('all')) {
      pending = pending.filter(t => selectedWallets.includes(t.wallet_id));
    }

    return pending.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getWalletName = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.name : 'Unknown';
  };

  const months = [
    { value: 'all', label: 'All Months' },
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const getAvailableYears = () => {
    const allDates = [
      ...income.map(i => new Date(i.date).getFullYear()),
      ...expenses.map(e => new Date(e.date).getFullYear()),
      ...householdExpenses.map(e => new Date(e.date).getFullYear())
    ];
    const uniqueYears = [...new Set(allDates)].sort((a, b) => b - a);
    return uniqueYears.length > 0 ? uniqueYears : [new Date().getFullYear()];
  };

  const transactions = getFilteredTransactions();
  const pending = getPendingTransactions();

  if (loading) {
    return (
      <div className="transactions-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-container">
      {/* Header Section */}
      <div className="transactions-header">
        <h1>📋 Transaction History</h1>
        <p className="viewing-text">Viewing: <strong>{getSelectedWalletsDisplay()}</strong></p>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Type:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="income">Income</option>
            <option value="expenses">Expenses</option>
            <option value="household">Household Only</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Month:</label>
          <div className="month-year-selector">
            <button 
              className="arrow-btn"
              onClick={() => {
                if (monthFilter === 'all') {
                  setMonthFilter('11');
                } else {
                  const newMonth = parseInt(monthFilter) - 1;
                  if (newMonth < 0) {
                    setMonthFilter('11');
                    setYearFilter(yearFilter - 1);
                  } else {
                    setMonthFilter(newMonth.toString());
                  }
                }
              }}
            >
              ◀
            </button>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
            <button 
              className="arrow-btn"
              onClick={() => {
                if (monthFilter === 'all') {
                  setMonthFilter('0');
                } else {
                  const newMonth = parseInt(monthFilter) + 1;
                  if (newMonth > 11) {
                    setMonthFilter('0');
                    setYearFilter(yearFilter + 1);
                  } else {
                    setMonthFilter(newMonth.toString());
                  }
                }
              }}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Year:</label>
          <div className="month-year-selector">
            <button 
              className="arrow-btn"
              onClick={() => {
                if (yearFilter === 'all') {
                  setYearFilter(new Date().getFullYear());
                } else {
                  setYearFilter(parseInt(yearFilter) - 1);
                }
              }}
            >
              ◀
            </button>
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="all">All Years</option>
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button 
              className="arrow-btn"
              onClick={() => {
                if (yearFilter === 'all') {
                  setYearFilter(new Date().getFullYear());
                } else {
                  setYearFilter(parseInt(yearFilter) + 1);
                }
              }}
            >
              ▶
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming Pending Transactions */}
      {pending.length > 0 && (
        <div className="pending-section">
          <h2>⏳ Upcoming Transactions (Next 7 Days)</h2>
          <div className="pending-list">
            {pending.map((transaction) => (
              <div key={`${transaction.type}-${transaction.id}`} className={`pending-card ${transaction.type}`}>
                <div className="pending-main">
                  <div className="pending-date">
                    <span className="date-day">{new Date(transaction.date).getDate()}</span>
                    <span className="date-month">{new Date(transaction.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                  </div>
                  
                  <div className="pending-info">
                    <div className="pending-header">
                      <span className="pending-category">
                        {transaction.type === 'income' ? transaction.source : transaction.category}
                      </span>
                      {transaction.subcategory && (
                        <span className="pending-subcategory">• {transaction.subcategory}</span>
                      )}
                      <span className={`pending-badge ${transaction.is_upcoming ? 'scheduled' : ''}`}>
                        {transaction.is_upcoming ? 'SCHEDULED' : 'PENDING'}
                      </span>
                    </div>
                    <div className="pending-details">
                      <span className="pending-description">
                        {transaction.description || 'No description'}
                      </span>
                      <span className="pending-wallet">{getWalletName(transaction.wallet_id)}</span>
                    </div>
                  </div>

                  <div className="pending-amount-section">
                    <span className={`pending-amount ${transaction.type}`}>
                      {transaction.type === 'income' ? '+' : '-'}{currencySymbol}
                      {transaction.type === 'income' 
                        ? parseFloat(transaction.net_amount).toFixed(2)
                        : parseFloat(getUserSplitAmount(transaction)).toFixed(2)}
                    </span>
                    {transaction.split_config && transaction.type === 'expenses' && (
                      <span className="split-indicator">
                        (your share of {currencySymbol}{parseFloat(transaction.amount).toFixed(2)})
                      </span>
                    )}
                  </div>

                  <div className="pending-actions">
                    {transaction.is_pending && (
                      <>
                        <button 
                          className="confirm-btn"
                          onClick={() => handleConfirmPending(transaction.id, transaction.type)}
                          title="Confirm now"
                        >
                          ✓ Confirm
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(transaction)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Transaction Table */}
      <div className="transactions-table-container">
        <h2>📋 Completed Transactions</h2>
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Category</th>
              <th>Description</th>
              <th>Pot</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  No transactions found
                </td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={`${transaction.type}-${transaction.id}`} className={transaction.type === 'household_expense' ? 'expense' : transaction.type}>
                  <td>{new Date(transaction.date).toLocaleDateString()}</td>
                  <td>
                    <span className={`type-badge ${transaction.type === 'household_expense' ? 'household' : transaction.type}`}>
                      {transaction.type === 'income' ? '💰 Income' : 
                       transaction.type === 'household_expense' ? '👥 Household' : '💸 Expense'}
                    </span>
                  </td>
                  <td>
                    {transaction.type === 'income' ? transaction.source : transaction.category}
                    {transaction.subcategory && (
                      <span className="subcategory-text"> • {transaction.subcategory}</span>
                    )}
                  </td>
                  <td className="description-cell">{transaction.description || 'No description'}</td>
                  <td>
                    {transaction.type === 'household_expense' 
                      ? (transaction.household_name || 'Household')
                      : getWalletName(transaction.wallet_id)
                    }
                  </td>
                  <td className={`amount ${transaction.type === 'household_expense' ? 'expense' : transaction.type}`}>
                    {transaction.type === 'income' ? '+' : '-'}{transaction.type === 'household_expense' ? (transaction.currency === 'GBP' ? '£' : '€') : currencySymbol}
                    {transaction.type === 'income' 
                      ? parseFloat(transaction.net_amount).toFixed(2)
                      : transaction.type === 'household_expense'
                        ? parseFloat(transaction.user_split_amount).toFixed(2)
                        : parseFloat(transaction.amount).toFixed(2)}
                    {transaction.type === 'household_expense' && (
                      <div className="split-indicator">
                        ({transaction.split_percentage}% of {transaction.currency === 'GBP' ? '£' : '€'}{parseFloat(transaction.amount).toFixed(2)})
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="view-btn"
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          setShowDetailModal(true);
                        }}
                        title="View details"
                      >
                        👁️
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(transaction)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          walletName={getWalletName(selectedTransaction.wallet_id)}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTransaction(null);
          }}
          onDelete={(transaction) => {
            handleDelete(transaction);
            setShowDetailModal(false);
            setSelectedTransaction(null);
          }}
          onTransactionUpdated={() => {
            fetchTransactions();
          }}
        />
      )}
    </div>
  );
}

export default Transactions;
